import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from 'app/routing/routesConfig';
import queryString from 'query-string';

import { useAppDispatch, useAppSelector } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { config } from 'config';
import { checkSubscriptionEffect } from 'app/store/Subscription/effects';
import {
  getManageSubscriptionLink,
  getSubscription,
  getSubscriptionLoading,
  getSubscriptionTiersDDL,
  getSubscriptionTiersDDLLink,
  getTierList,
  getTierListLink,
  getTierListLoading,
  getTiersDDLLoading,
} from 'app/store/Subscription/selectors';
import {
  subscriptionCancelledAction,
  subscriptionCreatedAction,
} from 'app/store/Mixpanel/slice';
import { isLoading } from 'app/utils/utils';

type CheckoutStatus = 'success' | 'cancel' | 'error';

export const useHandleSubscriptionPayload = (
  { disable } = {
    disable: false,
  }
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [
    subscriptionAlreadyRefetched,
    setSubscriptionAlreadyRefetched,
  ] = useState(false);

  const getCheckoutStatusMessage = (status: CheckoutStatus) => {
    if (status === 'success') {
      return 'You have subscribed to Session Studio!';
    }

    return 'Subscription request not complete.';
  };

  useEffect(() => {
    if (disable) {
      return;
    }
    const parsed = queryString.parse(location.search);
    const checkoutStatus = parsed?.checkout as CheckoutStatus;
    const sessionId = parsed?.session_id as string;

    const shouldRefetchSubscription =
      checkoutStatus === 'success' && config.isElectronBuild && sessionId;

    if (checkoutStatus) {
      const message = getCheckoutStatusMessage(checkoutStatus);

      if (shouldRefetchSubscription) {
        dispatch(checkSubscriptionEffect());
        setSubscriptionAlreadyRefetched(true);
      }

      if (message) {
        dispatch(
          addAlertAction({
            message,
            autoHideDuration: 4000,
          })
        );
      }

      if (checkoutStatus === 'success') {
        dispatch(subscriptionCreatedAction());
      } else if (checkoutStatus === 'cancel') {
        dispatch(subscriptionCancelledAction());
      }

      navigate({ search: '' }, { replace: true });
    }
  }, [dispatch, navigate, location.search, disable]);

  useEffect(() => {
    if (disable) {
      return;
    }
    const shouldRefetchSubscription =
      location.pathname.includes(routes.settings.path) &&
      config.isElectronBuild &&
      !location.search &&
      !subscriptionAlreadyRefetched;

    if (shouldRefetchSubscription) {
      dispatch(checkSubscriptionEffect());
      setSubscriptionAlreadyRefetched(false);
    }
  }, [dispatch, location, subscriptionAlreadyRefetched, disable]);
};

export const useSubscription = () => {
  const manageSubscriptionLink = useAppSelector(getManageSubscriptionLink);
  const tierListLink = useAppSelector(getTierListLink);
  const tierListLoadingState = useAppSelector(getTierListLoading);
  const tierDDLLoadingState = useAppSelector(getTiersDDLLoading);
  const subscriptionTiersDDLLink = useAppSelector(getSubscriptionTiersDDLLink);
  const tierList = useAppSelector(getTierList);
  const subscriptionTiersDDL = useAppSelector(getSubscriptionTiersDDL);
  const subscription = useAppSelector(getSubscription);
  const subscriptionLoading = useAppSelector(getSubscriptionLoading);

  return {
    manageSubscriptionLink,
    tierListLink,
    subscriptionTiersDDLLink,
    tierListLoading: isLoading(tierListLoadingState),
    subscriptionLoading: isLoading(subscriptionLoading),
    subscriptionTiersDDLLoading: isLoading(tierDDLLoadingState),
    tierList,
    subscription,
    subscriptionTiersDDL,
  };
};
